import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Text from 'components/atoms/Text';
import Button from 'components/atoms/Button';
import Input from 'components/atoms/Input';
import { COLOR_SYSTEM } from 'components/atoms';
import {
  ConsentInfo,
  NhisStatus,
  AUTH_METHODS,
  ConsentStatus,
  ConsentItemType,
  ConsentItemConfig,
  PASS_TELECOM_OPTIONS,
  PHONE_VERIFY_TELECOM_OPTIONS,
  REQUIRES_TELECOM,
  AuthMethod,
  TelecomOption,
  ConsentProfile,
} from 'types/nhis';
import { startNhisAuth } from 'api/nhisService';
import { log, LogLevel } from 'utils/LogUtil';
import SecureKeypad from 'components/molecules/SecureKeypad';
import { SlideManager } from 'components/organisms/CommonUI/SlidePopup/SlideManager';
import {
  CONSENT_ITEMS,
  CONSENT_ITEMS_CARINGNOTE,
  CONSENT_ITEMS_KMRS,
  CONSENT_VERSION,
  CONSENT_VERSION_CARINGNOTE,
  CONSENT_VERSION_KMRS,
} from 'common/constants';
import { CONSENT_DETAIL_COMPONENTS } from 'components/molecules/ConsentDetail';
import { StepIndicator } from './NhisLayout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoCard = styled.div`
  background: ${COLOR_SYSTEM.get('Blue')?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get('Blue')?.[200]};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
`;

const ConsentSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const AllConsentRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 0;
  cursor: pointer;

  input[type="checkbox"] {
    width: 22px;
    height: 22px;
    margin-top: 1px;
    flex-shrink: 0;
    accent-color: ${COLOR_SYSTEM.get('Blue')?.[500]};
  }
`;

const ConsentDivider = styled.hr`
  border: none;
  border-top: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  margin: 0;
`;

const ConsentItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
`;

const ConsentCheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  cursor: pointer;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    accent-color: ${COLOR_SYSTEM.get('Blue')?.[500]};
  }
`;

const DetailButton = styled.button`
  background: none;
  border: none;
  color: ${COLOR_SYSTEM.get('Blue')?.[500]};
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  margin: 8px 0;
`;

const AuthMethodList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AuthMethodItem = styled.label<{ $disabled?: boolean; $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[300] : COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 8px;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  background: ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[25] : '#ffffff'};
  transition: all 0.2s ease;
`;

const TelecomRow = styled.div`
  display: flex;
  gap: 8px;
`;

const TelecomChip = styled.button<{ $selected?: boolean }>`
  padding: 10px 16px;
  border: 1.5px solid ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[400] : COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 20px;
  background: ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[50] : '#ffffff'};
  color: ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[600] : COLOR_SYSTEM.get('Gray')?.[700]};
  font-size: 16px;
  font-weight: ${(props) => (props.$selected ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;
`;

const JuminInputRow = styled.div<{ $preview?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    ${({ $preview }) => !$preview && `font-size: 22px !important;`}
  }
`;

const ErrorMessage = styled.div`
  background: ${COLOR_SYSTEM.get('Red')?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get('Red')?.[200]};
  border-radius: 8px;
  padding: 12px 16px;
`;

/** 표시 중인 항목 목록에서 초기/전체동의 상태를 파생한다 — 항목 수 하드코딩 금지(spec 2026-09-02 §R-3). */
const buildConsentStatus = (items: { id: ConsentItemType }[], value: boolean): ConsentStatus =>
  Object.fromEntries(items.map((item) => [item.id, value])) as unknown as ConsentStatus;

/**
 * 동의 화면 판 결정(spec D28) — 백엔드 consentProfile을 우선 보고, 없으면 기존 isPartner 분기로 폴백한다.
 * consentProfile을 주지 않는 구버전 백엔드와 공존해야 하므로(배포 순서) 폴백 경로를 유지한다.
 */
const resolveConsentProfile = (consentInfo: ConsentInfo): ConsentProfile => {
  if (consentInfo.consentProfile === 'kmrs') return 'kmrs';
  if (consentInfo.consentProfile === 'caringnote') return 'caringnote';
  return consentInfo.isPartner ? 'caringnote' : 'default';
};

const CONSENT_ITEMS_BY_PROFILE: Record<ConsentProfile, ConsentItemConfig[]> = {
  kmrs: CONSENT_ITEMS_KMRS,
  caringnote: CONSENT_ITEMS_CARINGNOTE,
  default: CONSENT_ITEMS,
};

const CONSENT_VERSION_BY_PROFILE: Record<ConsentProfile, string> = {
  kmrs: CONSENT_VERSION_KMRS,
  caringnote: CONSENT_VERSION_CARINGNOTE,
  default: CONSENT_VERSION,
};

interface ConsentPageProps {
  consentInfo: ConsentInfo;
  token: string;
  onStatusChange: (status: NhisStatus, data?: any) => void;
  previewMode?: boolean;
}

const ConsentPage: React.FC<ConsentPageProps> = ({ consentInfo, token, onStatusChange, previewMode }) => {
  // 파트너별 동의 화면 판을 고른다(spec D28): kmrs → 서울시약사회·서울대판, caringnote → 늘픔가치판, 그 밖 → 기본 5항목판.
  const consentProfile = resolveConsentProfile(consentInfo);
  const consentItems = CONSENT_ITEMS_BY_PROFILE[consentProfile];
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() => buildConsentStatus(consentItems, false));
  const [selectedAuthMethod, setSelectedAuthMethod] = useState('PHONE_VERIFY');
  // PHONE_VERIFY 외 다른 인증수단(간편인증 5종) 펼치기 토글
  const [showOtherMethods, setShowOtherMethods] = useState(false);
  const [selectedTelecom, setSelectedTelecom] = useState<string | null>(null);
  const [juminFront, setJuminFront] = useState('');
  const [juminBack, setJuminBack] = useState('');
  const [showKeypad, setShowKeypad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (previewMode) return;
    SlideManager.show({
      body: (
        <div style={{ padding: '32px 24px', textAlign: 'center' }}>
          <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ display: 'block', marginBottom: '24px' }}>
            약사님께 처방내역을 전달하려면
            <br />
            총 3단계를 완료해주세요.
          </Text>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <StepIndicator step={0} />
          </div>
          <div style={{ marginTop: '24px' }}>
            <Button
              buttonType="button"
              color="Primary"
              size="Large"
              type="Text"
              variant="Contained"
              onClick={() => SlideManager.hide()}
            >
              시작하기
            </Button>
          </div>
        </div>
      ),
      direction: 'bottom',
      height: 'auto',
      needCloseButton: false,
      onClose: () => {},
    });
  }, [previewMode]);

  const selectedMethod = AUTH_METHODS.find(m => m.code === selectedAuthMethod);
  const needsTelecom = selectedMethod ? REQUIRES_TELECOM(selectedMethod) : false;
  const isPhoneVerify = selectedMethod?.authMethodType === 'PHONE_VERIFY';
  const telecomOptions: TelecomOption[] = isPhoneVerify
    ? PHONE_VERIFY_TELECOM_OPTIONS
    : PASS_TELECOM_OPTIONS;
  const allChecked = Object.values(consentStatus).every(Boolean);

  const isFormValid =
    allChecked &&
    selectedAuthMethod &&
    (!needsTelecom || selectedTelecom !== null) &&
    juminFront.length === 6 &&
    juminBack.length === 7;

  const handleAllConsentChange = () => {
    const newValue = !allChecked;
    setConsentStatus(buildConsentStatus(consentItems, newValue));
  };

  const handleItemChange = (id: ConsentItemType, checked: boolean) => {
    setConsentStatus((prev) => ({ ...prev, [id]: checked }));
  };

  const handleDetailClick = (consentType: ConsentItemType) => {
    const DetailComponent = CONSENT_DETAIL_COMPONENTS[consentType];
    SlideManager.show({
      body: <DetailComponent profile={consentProfile} />,
      direction: 'bottom',
      height: '70vh',
      needCloseButton: true,
      onClose: () => {},
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid || loading) return;

    setLoading(true);
    setError(null);

    const juminNum = juminFront + juminBack;

    const result = await startNhisAuth({
      token,
      authMethodType: isPhoneVerify ? 'PHONE_VERIFY' : 'SIMPLE',
      // SIMPLE일 때만 loginType 전송 (PHONE_VERIFY는 생략 — 가이드 3.1항)
      ...(isPhoneVerify ? {} : { loginType: selectedAuthMethod }),
      juminNum,
      // PHONE_VERIFY: 항상 필수 / SIMPLE+PASS: 필수 / 그 외 SIMPLE: 생략
      ...(needsTelecom && selectedTelecom !== null ? { telecomType: selectedTelecom } : {}),
      consent: {
        status: consentStatus,
        // 버전으로 판(기본 5항목 / 늘품 6항목 / K-MRS 6항목)을 구분한다(spec 확정 4·D28).
        // status 키가 곧 제출 키라, K-MRS판은 PARTNER_THIRD_PARTY, 늘품판은 CARINGNOTE_THIRD_PARTY로 나간다.
        version: CONSENT_VERSION_BY_PROFILE[consentProfile],
      },
    });

    // 주민번호 즉시 초기화 (보안)
    setJuminFront('');
    setJuminBack('');

    if (result.error) {
      log(LogLevel.ERROR, 'ConsentPage: 인증 시작 실패', result);

      setError(result.message || '인증 요청에 실패했습니다.');
      setLoading(false);
      return;
    }

    setLoading(false);
    onStatusChange('AUTH_REQUESTED', {
      authMethod: selectedAuthMethod,
      authMethodType: isPhoneVerify ? 'PHONE_VERIFY' : 'SIMPLE',
    });
  };

  return (
    <Container>
      <InfoCard>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[700]} style={{ fontSize: '17px' }}>
          안녕하세요! <strong>{consentInfo.patientName}</strong>님
          <br />
          <strong>{consentInfo.pharmacistName}</strong> 약사님이
          <br />
          약력 제공 및 분석 동의를 요청하셨습니다.
        </Text>
      </InfoCard>

      {/* 전체 동의 카드 */}
      <div
        onClick={handleAllConsentChange}
        style={{
          padding: '18px 16px',
          backgroundColor: allChecked ? COLOR_SYSTEM.get('Blue')?.[50] : '#ffffff',
          border: `2px solid ${allChecked ? COLOR_SYSTEM.get('Blue')?.[400] : COLOR_SYSTEM.get('Gray')?.[200]}`,
          borderRadius: '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}
      >
        <input
          type="checkbox"
          checked={allChecked}
          onChange={handleAllConsentChange}
          onClick={(e) => e.stopPropagation()}
          style={{ width: '24px', height: '24px', flexShrink: 0, accentColor: COLOR_SYSTEM.get('Blue')?.[500] }}
        />
        <div style={{ flex: 1 }}>
          <Text textType="H4" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ display: 'block' }}>
            전체 동의하기
          </Text>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]} style={{ marginTop: '4px', display: 'block' }}>
            아래 항목에 모두 동의합니다.
          </Text>
        </div>
        {previewMode && (
          <div
            style={{
              padding: '6px 12px',
              backgroundColor: COLOR_SYSTEM.get('Gray')?.[100],
              borderRadius: '6px',
              flexShrink: 0,
            }}
          >
            <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[600]}>
              세부내용 보기
            </Text>
          </div>
        )}
      </div>

      {/* 개별 동의 카드 (previewMode에서는 숨김) */}
      {!previewMode && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {consentItems.map((item) => (
            <div
              key={item.id}
              onClick={() => handleItemChange(item.id, !consentStatus[item.id])}
              style={{
                padding: '16px',
                backgroundColor: consentStatus[item.id] ? COLOR_SYSTEM.get('Blue')?.[25] : '#ffffff',
                border: `1.5px solid ${consentStatus[item.id] ? COLOR_SYSTEM.get('Blue')?.[300] : COLOR_SYSTEM.get('Gray')?.[200]}`,
                borderRadius: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
              }}
            >
              <input
                type="checkbox"
                checked={consentStatus[item.id]}
                onChange={(e) => handleItemChange(item.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '22px', height: '22px', flexShrink: 0, accentColor: COLOR_SYSTEM.get('Blue')?.[500] }}
              />
              <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[700]} style={{ flex: 1 }}>
                {item.label}
              </Text>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleDetailClick(item.id);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: COLOR_SYSTEM.get('Gray')?.[100],
                  borderRadius: '6px',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[600]}>
                  상세
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}

      <Divider />

      <div>
        <AuthMethodList>
          {AUTH_METHODS.map((method) => {
            const methodIsPhoneVerify = method.authMethodType === 'PHONE_VERIFY';
            const methodRequiresTelecom = REQUIRES_TELECOM(method);
            const methodTelecomOptions: TelecomOption[] = methodIsPhoneVerify
              ? PHONE_VERIFY_TELECOM_OPTIONS
              : PASS_TELECOM_OPTIONS;
            const isSelected = selectedAuthMethod === method.code;

            // PHONE_VERIFY는 항상 표시, 나머지 간편인증 5종은 showOtherMethods일 때만 표시
            // (previewMode에서는 약사 튜토리얼 목적이므로 모든 수단을 그대로 노출)
            if (!methodIsPhoneVerify && !showOtherMethods && !previewMode) return null;

            return (
            <React.Fragment key={method.code}>
              <AuthMethodItem
                $disabled={method.disabled}
                $selected={isSelected && !method.disabled}
                onClick={() => {
                  if (method.disabled) return;
                  setSelectedAuthMethod(method.code);
                  if (!methodRequiresTelecom) setSelectedTelecom(null);
                  // 인증 방식 교체 시 통신사 선택값 리셋 (PASS vs PHONE_VERIFY 코드 체계 상이)
                  else setSelectedTelecom(null);
                }}
                style={
                  (methodRequiresTelecom && isSelected) || method.subtitle
                    ? { flexWrap: 'wrap' }
                    : undefined
                }
              >
                <input
                  type="radio"
                  name="authMethod"
                  value={method.code}
                  checked={isSelected}
                  onChange={() => setSelectedAuthMethod(method.code)}
                  disabled={method.disabled}
                  style={{ display: 'none' }}
                />
                {method.icon && (
                  <img
                    src={method.icon}
                    alt={method.name}
                    style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }}
                  />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[700]} style={{ fontSize: '16px' }}>
                    {method.name}
                  </Text>
                  {method.subtitle && (
                    <Text
                      textType="Body2"
                      color={COLOR_SYSTEM.get('Gray')?.[500]}
                      style={{ fontSize: '13px', marginTop: '2px' }}
                    >
                      {method.subtitle}
                    </Text>
                  )}
                </div>
                {method.disabledLabel && (
                  <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[400]}>
                    ({method.disabledLabel})
                  </Text>
                )}
                {methodRequiresTelecom && isSelected && (
                  <div style={{ width: '100%', marginTop: '4px' }}>
                    <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]} style={{ marginBottom: '8px', display: 'block' }}>
                      통신사를 선택해주세요
                    </Text>
                    <TelecomRow
                      style={
                        methodIsPhoneVerify
                          ? { flexWrap: 'wrap', rowGap: '6px' }
                          : undefined
                      }
                    >
                      {methodTelecomOptions.map((t) => (
                        <TelecomChip
                          key={t.code}
                          type="button"
                          $selected={selectedTelecom === t.code}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTelecom(t.code);
                          }}
                          style={{
                            ...(methodIsPhoneVerify
                              ? { flex: '1 0 calc(33.333% - 6px)' }
                              : {}),
                            ...(previewMode
                              ? { fontSize: '13px', padding: '6px 10px' }
                              : {}),
                          }}
                        >
                          {t.name}
                        </TelecomChip>
                      ))}
                    </TelecomRow>
                  </div>
                )}
              </AuthMethodItem>
              {/* PHONE_VERIFY 아이템 뒤에 '다른 인증수단 선택하기' 토글 (previewMode에서는 숨김) */}
              {methodIsPhoneVerify && !previewMode && (
                <button
                  type="button"
                  onClick={() => setShowOtherMethods((v) => !v)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '14px 0',
                    cursor: 'pointer',
                    color: COLOR_SYSTEM.get('Gray')?.[600],
                    fontSize: '17px',
                    textAlign: 'center',
                    textDecoration: 'underline',
                    width: '100%',
                  }}
                >
                  {showOtherMethods
                    ? '다른 인증수단 접기'
                    : '다른 인증수단 선택하기'}
                </button>
              )}
            </React.Fragment>
            );
          })}
        </AuthMethodList>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
          이름
        </Text>
        <div
          style={{
            padding: '14px 16px',
            backgroundColor: COLOR_SYSTEM.get('Gray')?.[100],
            borderRadius: '12px',
            fontSize: previewMode ? '16px' : '22px',
            color: COLOR_SYSTEM.get('Gray')?.[600],
          }}
        >
          {consentInfo.patientName}
        </div>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Orange')?.[700]} style={{ marginTop: '8px', fontSize: '16px', lineHeight: '1.5' }}>
          위 정보가 본인과 다르면 약사님에게 다시 요청해주세요.
        </Text>
      </div>

      {consentInfo.maskedPhoneNumber && (
        <div style={{ marginBottom: '16px' }}>
          <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
            전화번호
          </Text>
          <div
            style={{
              padding: '14px 16px',
              backgroundColor: COLOR_SYSTEM.get('Gray')?.[100],
              borderRadius: '12px',
              fontSize: '22px',
              color: COLOR_SYSTEM.get('Gray')?.[600],
            }}
          >
            {consentInfo.maskedPhoneNumber}
          </div>
        </div>
      )}

      <div>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
          주민등록번호
        </Text>
        <JuminInputRow $preview={previewMode}>
          <Input
            placeholder="앞 6자리"
            value={juminFront}
            inputMode="numeric"
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setJuminFront(val);
              if (val.length === 6) {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
                setTimeout(() => setShowKeypad(true), 100);
              }
            }}
            maxLength={6}
            style={{ flex: 1 }}
          />
          <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[400]}>-</Text>
          <Input
            type="text"
            placeholder="뒤 7자리"
            value={'●'.repeat(juminBack.length)}
            readonly
            inputMode="none"
            onFocus={() => {
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            }}
            onClick={() => setShowKeypad(true)}
            onPaste={(e) => e.preventDefault()}
            style={{ flex: 1 }}
          />
        </JuminInputRow>
      </div>

      {error && (
        <ErrorMessage>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Red')?.[600]}>
            {error}
          </Text>
        </ErrorMessage>
      )}

      <Button
        buttonType="button"
        color="Primary"
        size="Large"
        type="Text"
        variant="Contained"
        onClick={handleSubmit}
        disabled={!isFormValid || loading}
        disabledColor={!isFormValid || loading}
      >
        {loading ? '처리 중...' : '다음 단계로'}
      </Button>

      {showKeypad && (
        <SecureKeypad
          maxLength={7}
          onComplete={(val) => {
            setJuminBack(val);
            setShowKeypad(false);
          }}
          onClose={() => setShowKeypad(false)}
        />
      )}
    </Container>
  );
};

export default ConsentPage;
